import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Moment } from 'moment-timezone';
import { ExtendedDeviceEUIProps, FridgeTemp, ParsedSensorInfo } from '../atoms-types/eSensors';
import EsensorChartHandler from '../handlers/eSensorGraph-handler';
import HvacSelectedPanel from './HvacSelectedPanel';
import HvacUnitCard from './HvacUnitCard';
import {
  getDisplayClass,
  getHvacStatus,
  HVAC_STATUS_COLORS,
  isCoolerClass,
  isFreezerClass,
} from './hvacStatus';

export type HvacGridFilter =
  | 'all'
  | 'warnings'
  | 'freezers'
  | 'coolers'
  | 'offline'
  | 'highest'
  | 'lowest';

interface HvacDashboardProps {
  sensors: ParsedSensorInfo[];
  isLoading: boolean;
  selectedSensor: ExtendedDeviceEUIProps | null;
  onSelectSensor: (sensor: ParsedSensorInfo) => void;
  startDateTime: Moment;
  endDateTime: Moment;
  setStartDateTime: (date: Moment) => void;
  setEndDateTime: (date: Moment) => void;
  selectedRange: string;
  setSelectedRange: (range: string) => void;
  canEditSensors: boolean;
  onEditSensors: () => void;
}

const FILTERS: { id: HvacGridFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'warnings', label: 'Warnings' },
  { id: 'freezers', label: 'Freezers' },
  { id: 'coolers', label: 'Coolers' },
  { id: 'offline', label: 'Offline' },
  { id: 'highest', label: 'Highest Temp' },
  { id: 'lowest', label: 'Lowest Temp' },
];

const SummaryChip: React.FC<{
  label: string;
  value: string | number;
  color?: string;
  outlined?: boolean;
}> = ({ label, value, color, outlined }) => (
  <Chip
    variant={outlined ? 'outlined' : 'filled'}
    label={
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {color ? (
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
        ) : null}
        <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
        <Typography component="span" variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    }
    sx={{
      height: 36,
      borderRadius: 1.5,
      bgcolor: outlined ? 'transparent' : 'background.paper',
      borderColor: 'divider',
      '& .MuiChip-label': { px: 1.25 },
    }}
  />
);

const HvacDashboard: React.FC<HvacDashboardProps> = ({
  sensors,
  isLoading,
  selectedSensor,
  onSelectSensor,
  startDateTime,
  endDateTime,
  setStartDateTime,
  setEndDateTime,
  selectedRange,
  setSelectedRange,
  canEditSensors,
  onEditSensors,
}) => {
  const [filter, setFilter] = useState<HvacGridFilter>('all');
  const [gridOpen, setGridOpen] = useState(true);

  const summary = useMemo(() => {
    let healthy = 0;
    let warning = 0;
    let alarm = 0;
    let offline = 0;
    let tempSum = 0;
    let tempCount = 0;

    sensors.forEach((sensor) => {
      const tempF = (sensor.jInfo as FridgeTemp | undefined)?.TempF;
      const status = getHvacStatus(
        typeof tempF === 'number' ? tempF : null,
        sensor.LastRx,
        { warningF: sensor.warningF, alarmF: sensor.alarmF },
      );
      if (status === 'normal') healthy += 1;
      else if (status === 'warning') warning += 1;
      else if (status === 'alarm') alarm += 1;
      else offline += 1;
      if (typeof tempF === 'number') {
        tempSum += tempF;
        tempCount += 1;
      }
    });

    const online = sensors.length - offline;
    return {
      total: sensors.length,
      healthy,
      warning,
      alarm,
      offline,
      avgTemp: tempCount ? tempSum / tempCount : null,
      onlinePct: sensors.length ? Math.round((online / sensors.length) * 100) : 0,
    };
  }, [sensors]);

  const filteredSensors = useMemo(() => {
    const withTemp = sensors.map((sensor) => {
      const tempF = (sensor.jInfo as FridgeTemp | undefined)?.TempF;
      const status = getHvacStatus(
        typeof tempF === 'number' ? tempF : null,
        sensor.LastRx,
        { warningF: sensor.warningF, alarmF: sensor.alarmF },
      );
      const classLabel = getDisplayClass(sensor);
      return { sensor, tempF: typeof tempF === 'number' ? tempF : null, status, classLabel };
    });

    let list = withTemp;
    switch (filter) {
      case 'warnings':
        list = withTemp.filter((s) => s.status === 'warning' || s.status === 'alarm');
        break;
      case 'freezers':
        list = withTemp.filter((s) => isFreezerClass(s.classLabel));
        break;
      case 'coolers':
        list = withTemp.filter((s) => isCoolerClass(s.classLabel) && !isFreezerClass(s.classLabel));
        break;
      case 'offline':
        list = withTemp.filter((s) => s.status === 'offline');
        break;
      case 'highest':
        list = [...withTemp].sort((a, b) => (b.tempF ?? -Infinity) - (a.tempF ?? -Infinity));
        break;
      case 'lowest':
        list = [...withTemp].sort((a, b) => (a.tempF ?? Infinity) - (b.tempF ?? Infinity));
        break;
      default:
        // Prefer problem units first so scanners see alarms immediately.
        list = [...withTemp].sort((a, b) => {
          const rank = { alarm: 0, warning: 1, offline: 2, normal: 3 } as const;
          return rank[a.status] - rank[b.status];
        });
        break;
    }

    return list.map((s) => s.sensor);
  }, [sensors, filter]);

  const selectedParsed = useMemo(() => {
    if (!selectedSensor) return null;
    return sensors.find((s) => s.deviceEUI === selectedSensor.deviceEUI) ?? null;
  }, [sensors, selectedSensor]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: { xs: 1.5, md: 2 },
        pb: 4,
        minHeight: '100%',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          HVAC Dashboard
        </Typography>
        {canEditSensors ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={onEditSensors}
            disabled={sensors.length === 0}
          >
            Edit Sensors
          </Button>
        ) : null}
      </Box>

      {/* Summary chips */}
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" width={110} height={36} />
          ))
        ) : (
          <>
            <SummaryChip label="Units" value={summary.total} outlined />
            <SummaryChip label="Healthy" value={summary.healthy} color={HVAC_STATUS_COLORS.normal} />
            <SummaryChip label="Warning" value={summary.warning} color={HVAC_STATUS_COLORS.warning} />
            <SummaryChip label="Alarm" value={summary.alarm} color={HVAC_STATUS_COLORS.alarm} />
            <SummaryChip label="Offline" value={summary.offline} color={HVAC_STATUS_COLORS.offline} />
            <SummaryChip
              label="Avg"
              value={summary.avgTemp === null ? '—' : `${summary.avgTemp.toFixed(1)}°F`}
              outlined
            />
            <SummaryChip label="Online" value={`${summary.onlinePct}%`} outlined />
          </>
        )}
      </Stack>

      {/* Selected unit + history — primary viewport */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '280px 1fr', lg: '300px 1fr' },
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        <HvacSelectedPanel
          sensor={selectedParsed}
          startDateTime={startDateTime}
          endDateTime={endDateTime}
        />
        <Box
          sx={{
            minHeight: { xs: 360, md: 420 },
            '& .MuiCard-root': { height: '100%' },
          }}
        >
          <EsensorChartHandler
            deviceEUI={selectedSensor?.deviceEUI || ''}
            Display={selectedSensor?.Display || ['TempF']}
            subClass={null}
            sensorColor={
              selectedParsed
                ? HVAC_STATUS_COLORS[
                    getHvacStatus(
                      (selectedParsed.jInfo as FridgeTemp | undefined)?.TempF,
                      selectedParsed.LastRx,
                      { warningF: selectedParsed.warningF, alarmF: selectedParsed.alarmF },
                    )
                  ]
                : selectedSensor?.sensorColor
            }
            sensorInfo={selectedParsed ?? undefined}
            startDateTime={startDateTime}
            endDateTime={endDateTime}
            setStartDateTime={setStartDateTime}
            setEndDateTime={setEndDateTime}
            selectedRange={selectedRange}
            setSelectedRange={setSelectedRange}
            chartTitle="Temperature History"
            compact
          />
        </Box>
      </Box>

      {/* Compact unit grid */}
      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mb: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Unit Grid
            </Typography>
            <IconButton size="small" onClick={() => setGridOpen((v) => !v)} aria-label="Toggle unit grid">
              {gridOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            {FILTERS.map((f) => (
              <Chip
                key={f.id}
                label={f.label}
                size="small"
                clickable
                color={filter === f.id ? 'primary' : 'default'}
                variant={filter === f.id ? 'filled' : 'outlined'}
                onClick={() => setFilter(f.id)}
              />
            ))}
          </Stack>
        </Box>

        <Collapse in={gridOpen}>
          {isLoading ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 1.25,
              }}
            >
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={148} />
              ))}
            </Box>
          ) : filteredSensors.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No units match this filter.
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 1.25,
                alignItems: 'stretch',
              }}
            >
              {filteredSensors.map((sensor) => (
                <HvacUnitCard
                  key={sensor.deviceEUI}
                  sensor={sensor}
                  selected={selectedSensor?.deviceEUI === sensor.deviceEUI}
                  startDateTime={startDateTime}
                  endDateTime={endDateTime}
                  onSelect={() => onSelectSensor(sensor)}
                />
              ))}
            </Box>
          )}
        </Collapse>
      </Box>
    </Box>
  );
};

export default HvacDashboard;
